import { AppSelect } from '../components/ui/AppSelect';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, isDomesticOfficialSeedance } from '../utils/domesticSeedanceCapabilities';
import { Modal } from '../components/ui/Modal';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  FileJson,
  FileText,
  Loader2,
  Play,
  RefreshCw,
  ServerCog,
  Terminal,
  XCircle,
} from "lucide-react";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { useToast } from "../context/ToastContext";
import {
  DREAMFLY_DEFAULT_IMAGE_MODEL_ID,
  DREAMFLY_IMAGE_MODELS,
  DREAMFLY_IMAGE_PROVIDER,
} from "../constants/dreamflyModels";
import { fetchDreamflyImageModels } from "../services/dreamflyModelService";
import { SEEDANCE25_PROVIDER_OPTIONS, getSeedance25Capabilities, isModelArkSeedance25Provider, normalizeSeedance25SubmissionProvider } from "../utils/seedance25Capabilities";
import {
  getProbeFixedVideoProvider,
  normalizeProbeVideoParams,
  resolveProbeVideoTask,
} from "../utils/providerProbeVideo";
import { queryVideoStatus } from "../services/videoService";
import { DATASTART_IMAGE_MODELS } from '../constants/datastartImageModels';
import { ModelArkReferenceLibraryPanel } from '../components/assets/ModelArkLibraryControls';
import { ASTRAFLOW_IMAGE_MODELS } from '../constants/astraflowImageModels';
import { ASTRAFLOW_ASTRA_MODELS, isAstraCombinedProvider, normalizeAstraProviderId } from '../constants/astraflowAstraModels';

const CATEGORY_LABELS = {
  text: "文字",
  image: "图片",
  video: "视频",
  audio: "音频",
};

const STATUS_STYLE = {
  idle: { label: "未测试", color: "var(--color-text-muted)", bg: "var(--color-bg-tertiary)" },
  running: { label: "运行中", color: "var(--color-warning)", bg: "color-mix(in srgb, var(--color-warning) 12%, transparent)" },
  success: { label: "成功", color: "var(--color-success)", bg: "color-mix(in srgb, var(--color-success) 12%, transparent)" },
  failed: { label: "失败", color: "var(--color-error)", bg: "color-mix(in srgb, var(--color-error) 12%, transparent)" },
};

const APP_PROVIDER_LABELS = [
  { id: VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, name: 'Seedance 2.0（国内官方）', desc: '官方 2.0 / Fast / Mini，国内官方素材库' },
  ...ASTRAFLOW_IMAGE_MODELS.map(({ id, label, model }) => ({ id, name: label, desc: `${model} · 纯图片 / 多图参考` })),
  ...DATASTART_IMAGE_MODELS.map(({ id, label, model }) => ({ id, name: label, desc: `${model} · 纯图片 / 单参考图 / 蒙版编辑` })),
  ...SEEDANCE25_PROVIDER_OPTIONS.map(({ value, label }) => ({
    id: value,
    name: label,
    desc: "Seedance 2.5 独立视频线路 · 参考图原始 URL 直传",
  })),
  { id: "wangsugem", name: "Gemini_WANGSU", desc: "Gemini-3.5 生图/分镜" },
  { id: "wangsugpt", name: "ChatGPT_WANGSU", desc: "GPT-5.5 / Image-2" },
  { id: "huobao", name: "Gemini_HUO", desc: "Gemini 中转站，多模型支持" },
  { id: "huobao_gpt", name: "ChatGPT_HUO", desc: "GPT-5.5 / Image 2" },
  { id: "huobao_gpt_image_cheap", name: "ChatGPT_HUO_4K", desc: "GPT-5.5 / gpt-image-2-count 4K低价图" },
  { id: "astraflow_gemini", name: "Gemini_AstraFlow", desc: "UCloud ModelVerse Gemini 协议" },
  { id: "astraflow_openai", name: "ChatGPT_AstraFlow", desc: "GPT-5.5 / Image-2" },
  { id: "astraflow_openai_luna", name: "GPT-5.6 Luna_AstraFlow", desc: "GPT-5.6 Luna / Image-2" },
  { id: "astraflow_openai_terra", name: "GPT-5.6 Terra_AstraFlow", desc: "GPT-5.6 Terra / Image-2" },
  { id: "astraflow_openai_sol", name: "GPT-5.6 Sol_AstraFlow", desc: "GPT-5.6 Sol / Image-2" },
  ...ASTRAFLOW_ASTRA_MODELS.map(({ id, label, model }) => ({ id, name: label, desc: `GPT-6 Astra / ${model}` })),
  { id: "astraflow_grok", name: "Grok_AstraFlow", desc: "Grok-4.5 / Grok Imagine Quality" },
  { id: "omini", name: "Gemini_OMINI", desc: "Omini Gemini 协议" },
  { id: "omini_openai", name: "ChatGPT_OMINI", desc: "Omini OpenAI 协议，GPT-5.5 / Image-2" },
  { id: "dreamfly_llm", name: "Dreamfly LLM", desc: "Dreamfly 独立文字线路，子模型由远端目录动态加载" },
  { id: "dreamfly_image", name: "Image_Dreamfly", desc: "Dreamfly 独立图片生成、参考生图与编辑线路" },
  { id: "doubao", name: "豆包", desc: "字节/即梦技术栈" },
  { id: "wanx", name: "通义万相 (Wanx)", desc: "阿里生图/修图" },
  { id: "deepseek", name: "DeepSeek & Vidu", desc: "DeepSeek推理分镜，Vidu生成图片" },
  { id: "claude_opus_mj", name: "Claude Opus + MJ", desc: "Opus 4.8 文本，Midjourney 生图" },
  { id: "moyuto_gpt", name: "ChatGPT_Moyuto", desc: "GPT-4o-mini / GPT Image 1" },
  { id: "mock_agent", name: "演示模式", desc: "使用示例数据快速体验流程" },
];

const providerLabelMap = new Map(APP_PROVIDER_LABELS.map((item) => [item.id, item]));
const MODELARK_KEY_SOURCE_LABELS = {
  runtime: "后台在线覆盖",
  environment: "环境变量",
  private_file: "私有配置文件",
  application: "应用配置",
  unconfigured: "未配置",
};

const DREAMFLY_IMAGE_PROVIDER_ID = DREAMFLY_IMAGE_PROVIDER.toLowerCase();
const FALLBACK_DREAMFLY_IMAGE_MODELS = DREAMFLY_IMAGE_MODELS.map(({ model, label }) => ({
  id: model,
  label,
}));
const DEFAULT_DREAMFLY_IMAGE_MODEL = DREAMFLY_IMAGE_MODELS.find(
  ({ id }) => id === DREAMFLY_DEFAULT_IMAGE_MODEL_ID,
)?.model || "gpt-image-2";
const isDreamflyImageProvider = (value) => (
  String(value || "").trim().toLowerCase() === DREAMFLY_IMAGE_PROVIDER_ID
);

const normalizeProviders = (providers = []) => [...new Map(providers.filter((item) => !getSeedance25Capabilities(item.id || item.name)?.retired).map((item) => {
  const sourceId = item.id || item.name;
  const id = isAstraCombinedProvider(sourceId)
    ? normalizeAstraProviderId(sourceId)
    : (normalizeSeedance25SubmissionProvider(sourceId) || sourceId);
  return [id, {
    id,
    name: providerLabelMap.get(id)?.name || item.name || item.id,
    desc: providerLabelMap.get(id)?.desc || item.desc || "",
  }];
})).values()];

const pretty = (value) => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? "");
  }
};

const RAW_TEXT_PARAM_KEYS = ["sourceText", "script", "prompt", "text"];

const textInputKeyForCase = (item) => {
  if (!item || item.category !== "text") return "";
  if (item.id === "text_long") return "sourceText";
  const params = item.defaultParams || {};
  return RAW_TEXT_PARAM_KEYS.find((key) => {
    const value = params[key];
    return typeof value === "string" && value.length > 80;
  }) || "";
};

const defaultDraft = (item) => {
  const textKey = textInputKeyForCase(item);
  if (textKey) return item?.defaultParams?.[textKey] || "";
  return JSON.stringify(item?.defaultParams || {}, null, 2);
};

const truncate = (value, len = 180) => {
  const raw = String(value || "");
  if (raw.length <= len) return raw;
  return `${raw.slice(0, len)}...`;
};

const looksLikeBase64Image = (value) => {
  const raw = String(value || "").trim();
  if (raw.length < 200) return false;
  return /^[A-Za-z0-9+/=\s]+$/.test(raw.slice(0, 1200));
};

const imageSrcFromValue = (value, key = "") => {
  if (typeof value !== "string") return "";
  const raw = value.trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:image/")) {
    return raw;
  }
  const keyHint = key.toLowerCase();
  if ((keyHint.includes("base64") || keyHint.includes("b64")) && looksLikeBase64Image(raw)) {
    return `data:image/png;base64,${raw.replace(/\s/g, "")}`;
  }
  return "";
};

const findImagePreviewSrc = (value, depth = 0) => {
  if (!value || depth > 5) return "";
  if (typeof value === "string") return imageSrcFromValue(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const src = findImagePreviewSrc(item, depth + 1);
      if (src) return src;
    }
    return "";
  }
  if (typeof value !== "object") return "";

  const priorityKeys = ["url", "imageUrl", "resultUrl", "thumbnailUrl", "base64", "b64_json", "b64Json"];
  for (const key of priorityKeys) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      const src = imageSrcFromValue(value[key], key) || findImagePreviewSrc(value[key], depth + 1);
      if (src) return src;
    }
  }
  for (const [key, child] of Object.entries(value)) {
    const src = imageSrcFromValue(child, key) || findImagePreviewSrc(child, depth + 1);
    if (src) return src;
  }
  return "";
};

const StatusPill = ({ status }) => {
  const style = STATUS_STYLE[status || "idle"] || STATUS_STYLE.idle;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium"
      style={{ color: style.color, backgroundColor: style.bg }}
    >
      {status === "running" && <Loader2 size={12} className="animate-spin" />}
      {status === "success" && <CheckCircle2 size={12} />}
      {status === "failed" && <XCircle size={12} />}
      {(!status || status === "idle") && <Clock size={12} />}
      {style.label}
    </span>
  );
};

const ProviderProbePage = () => {
  const toast = useToast();
  const [providers, setProviders] = useState([]);
  const [cases, setCases] = useState([]);
  const [provider, setProvider] = useState(() => normalizeAstraProviderId(localStorage.getItem("ai_provider") || "wangsugem"));
  const [projectId, setProjectId] = useState("");
  const [episodeId, setEpisodeId] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [paramDrafts, setParamDrafts] = useState({});
  const [results, setResults] = useState({});
  const [runningIds, setRunningIds] = useState(new Set());
  const [queryingVideoTasks, setQueryingVideoTasks] = useState(() => new Set());
  const [loadingCases, setLoadingCases] = useState(false);
  const [dreamflyImageModels, setDreamflyImageModels] = useState(FALLBACK_DREAMFLY_IMAGE_MODELS);
  const [dreamflyImageModel, setDreamflyImageModel] = useState(DEFAULT_DREAMFLY_IMAGE_MODEL);
  const [loadingDreamflyImageModels, setLoadingDreamflyImageModels] = useState(false);
  const [dreamflyImageModelError, setDreamflyImageModelError] = useState("");
  const [configurationCheckOpen, setConfigurationCheckOpen] = useState(false);
  const [configurationCheck, setConfigurationCheck] = useState(null);

  const isDreamflyImage = isDreamflyImageProvider(provider);
  const effectiveDreamflyImageModel = dreamflyImageModels.some(({ id }) => id === dreamflyImageModel)
    ? dreamflyImageModel
    : (
      dreamflyImageModels.find(({ id }) => id === DEFAULT_DREAMFLY_IMAGE_MODEL)?.id
      || dreamflyImageModels[0]?.id
      || DEFAULT_DREAMFLY_IMAGE_MODEL
    );

  const selectedCase = useMemo(
    () => cases.find((item) => item.id === selectedCaseId) || cases[0],
    [cases, selectedCaseId]
  );

  const selectedResult = selectedCase ? results[selectedCase.id] : null;
  const fixedVideoProvider = getProbeFixedVideoProvider(selectedCase?.id);
  const selectedModelarkProvider = isDomesticOfficialSeedance(fixedVideoProvider || provider) ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : normalizeSeedance25SubmissionProvider(fixedVideoProvider || provider);
  const isOfficialModelarkSelected = isModelArkSeedance25Provider(selectedModelarkProvider) || isDomesticOfficialSeedance(selectedModelarkProvider);
  const isDomesticAssetLibrarySelected = isDomesticOfficialSeedance(selectedModelarkProvider);
  const selectedVideoTask = resolveProbeVideoTask(selectedResult);
  const selectedTextInputKey = selectedCase ? textInputKeyForCase(selectedCase) : "";
  const selectedModelarkReferences = useMemo(() => {
    if (!isDomesticAssetLibrarySelected || selectedCase?.category !== 'video') return [];
    try {
      const params = JSON.parse(paramDrafts[selectedCase.id] || '{}');
      return [params.referenceImageUrl, params.startImageUrl, params.endImageUrl,
        ...(Array.isArray(params.referenceUrls) ? params.referenceUrls : []),
        ...(Array.isArray(params.referenceImages) ? params.referenceImages : []),
        ...(Array.isArray(params.guaVideoContents) ? params.guaVideoContents : []),
      ].filter(Boolean);
    } catch { return []; }
  }, [isDomesticAssetLibrarySelected, paramDrafts, selectedCase]);
  const selectedPreviewSrc = useMemo(
    () => findImagePreviewSrc(selectedResult?.result),
    [selectedResult]
  );

  const filteredCases = useMemo(() => {
    if (category === "all") return cases;
    return cases.filter((item) => item.category === category);
  }, [cases, category]);

  const loadCases = useCallback(async () => {
    setLoadingCases(true);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/admin/provider-probe/cases`);
      const nextCases = data?.cases || [];
      setProviders(normalizeProviders(data?.providers || []));
      setCases(nextCases);
      setSelectedCaseId((prev) => prev || nextCases[0]?.id || "");
      setParamDrafts((prev) => {
        const next = { ...prev };
        nextCases.forEach((item) => {
          if (!next[item.id]) next[item.id] = defaultDraft(item);
        });
        return next;
      });
    } catch (error) {
      toast.error(`加载代理测试项失败: ${error.message}`);
    } finally {
      setLoadingCases(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (!isDreamflyImage) return undefined;

    let cancelled = false;
    setLoadingDreamflyImageModels(true);
    setDreamflyImageModelError("");
    fetchDreamflyImageModels()
      .then((catalog) => {
        if (cancelled) return;
        const nextModels = catalog?.models?.length
          ? catalog.models
          : FALLBACK_DREAMFLY_IMAGE_MODELS;
        setDreamflyImageModels(nextModels);
        setDreamflyImageModel((current) => {
          if (nextModels.some(({ id }) => id === current)) return current;
          if (nextModels.some(({ id }) => id === catalog?.defaultModel)) {
            return catalog.defaultModel;
          }
          return nextModels.find(({ id }) => id === DEFAULT_DREAMFLY_IMAGE_MODEL)?.id
            || nextModels[0]?.id
            || DEFAULT_DREAMFLY_IMAGE_MODEL;
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setDreamflyImageModels(FALLBACK_DREAMFLY_IMAGE_MODELS);
        setDreamflyImageModel((current) => (
          FALLBACK_DREAMFLY_IMAGE_MODELS.some(({ id }) => id === current)
            ? current
            : DEFAULT_DREAMFLY_IMAGE_MODEL
        ));
        setDreamflyImageModelError(`模型目录加载失败，已使用内置列表：${error.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingDreamflyImageModels(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isDreamflyImage]);

  const parseParams = useCallback((item) => {
    const textKey = textInputKeyForCase(item);
    const raw = paramDrafts[item.id] || (textKey ? "" : "{}");
    let params;
    if (textKey) {
      params = {
        ...(item.defaultParams || {}),
        [textKey]: raw,
      };
    } else {
      try {
        params = JSON.parse(raw);
      } catch (error) {
        throw new Error(`参数 JSON 解析失败: ${error.message}`);
      }
    }

    if (isDreamflyImage && item.category === "image") {
      return {
        ...params,
        model: effectiveDreamflyImageModel,
      };
    }
    return normalizeProbeVideoParams(item.id, params);
  }, [effectiveDreamflyImageModel, isDreamflyImage, paramDrafts]);

  const setCaseResult = (caseId, result) => {
    setResults((prev) => ({ ...prev, [caseId]: result }));
  };

  const runCase = useCallback(async (item) => {
    if (!item?.id) return null;
    const runProvider = getProbeFixedVideoProvider(item.id) || provider;
    setSelectedCaseId(item.id);
    setRunningIds((prev) => new Set([...prev, item.id]));
    const startedAt = new Date().toISOString();
    let params;
    try {
      params = parseParams(item);
    } catch (error) {
      const failed = {
        caseId: item.id,
        status: "failed",
        startedAt,
        finishedAt: new Date().toISOString(),
        durationMs: 0,
        request: { caseId: item.id, provider: runProvider, projectId, episodeId, paramsText: paramDrafts[item.id] },
        error: { message: error.message, className: "FrontendParamError", stackTrace: error.stack },
      };
      setCaseResult(item.id, failed);
      setRunningIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      return failed;
    }

    try {
      const result = await authenticatedFetch(`${API_HOST}/api/admin/provider-probe/run`, {
        method: "POST",
        body: JSON.stringify({
          caseId: item.id,
          provider: runProvider,
          projectId: projectId ? Number(projectId) : null,
          episodeId: episodeId ? Number(episodeId) : null,
          params,
        }),
        timeout: 1800000,
      });
      setCaseResult(item.id, result);
      return result;
    } catch (error) {
      const failed = {
        caseId: item.id,
        status: "failed",
        startedAt,
        finishedAt: new Date().toISOString(),
        durationMs: 0,
        request: { caseId: item.id, provider: runProvider, projectId, episodeId, params },
        error: {
          message: error.message,
          className: error.name || "FetchError",
          stackTrace: error.stack,
          raw: error.data || null,
        },
      };
      setCaseResult(item.id, failed);
      return failed;
    } finally {
      setRunningIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, [episodeId, paramDrafts, parseParams, projectId, provider]);

  const querySubmittedVideo = useCallback(async (item) => {
    const task = resolveProbeVideoTask(results[item?.id]);
    if (!task) return;
    const taskKey = `${task.provider}:${task.taskId}`;
    setQueryingVideoTasks((current) => new Set([...current, taskKey]));
    try {
      const videoTaskStatus = await queryVideoStatus(task.provider, task.taskId, task.extra);
      setResults((current) => {
        // A late query for an older run must never overwrite a newly submitted task.
        if (resolveProbeVideoTask(current[item.id])?.taskId !== task.taskId) return current;
        return { ...current, [item.id]: { ...current[item.id], videoTaskStatus } };
      });
    } catch (error) {
      toast.error(`查询视频任务失败: ${error.message}`);
    } finally {
      setQueryingVideoTasks((current) => {
        const next = new Set(current);
        next.delete(taskKey);
        return next;
      });
    }
  }, [results, toast]);

  const runAll = useCallback(async () => {
    for (const item of filteredCases) {
      const result = await runCase(item);
      if (result?.status === "failed") {
        toast.info(`已停在失败项: ${item.name}`);
        break;
      }
    }
  }, [filteredCases, runCase, toast]);

  const resetParams = () => {
    if (!selectedCase) return;
    setParamDrafts((prev) => ({ ...prev, [selectedCase.id]: defaultDraft(selectedCase) }));
  };

  const copyDetail = async (value) => {
    try {
      await navigator.clipboard.writeText(pretty(value));
      toast.success("已复制");
    } catch {
      toast.error("复制失败");
    }
  };

  const checkModelarkConfiguration = async () => {
    if (!isModelArkSeedance25Provider(selectedModelarkProvider) && !isDomesticOfficialSeedance(selectedModelarkProvider)) return;
    const checkedProvider = selectedModelarkProvider;
    setConfigurationCheckOpen(true);
    setConfigurationCheck({ provider: checkedProvider, loading: true });
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/admin/modelark/configuration`, { method: "GET" });
      const configuration = data?.providers?.find((item) => item.provider === checkedProvider);
      if (!configuration) throw new Error("服务器未返回此线路的配置，请确认后端已更新");
      setConfigurationCheck({
        provider: checkedProvider,
        loading: false,
        configuration,
        privateConfigAvailable: data.privateConfigAvailable,
        notes: Array.isArray(data.notes) ? data.notes : [],
      });
    } catch (error) {
      setConfigurationCheck({ provider: checkedProvider, loading: false, error: error.message || "配置检查失败" });
    }
  };

  return (
    <div className="provider-probe-page">
      <style>{`
        .provider-probe-page {
          width: 100%;
          height: 100vh;
          min-height: 100vh;
          overflow: hidden;
          display: grid;
          grid-template-rows: auto 1fr;
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
        }
        .probe-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 14px 18px;
          border-bottom: 1px solid var(--color-border-primary);
          background: var(--color-bg-secondary);
        }
        .probe-title {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 220px;
        }
        .probe-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .probe-input, .probe-select, .probe-textarea {
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-tertiary);
          color: var(--color-text-primary);
          border-radius: 8px;
          outline: none;
        }
        .probe-input, .probe-select {
          height: 34px;
          padding: 0 10px;
          font-size: 12px;
        }
        .probe-button {
          height: 34px;
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-tertiary);
          color: var(--color-text-primary);
          border-radius: 8px;
          padding: 0 12px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          transition: background .16s, border-color .16s;
        }
        .probe-button:hover {
          background: var(--color-bg-hover);
          border-color: var(--color-accent-primary);
        }
        .probe-button.primary {
          background: var(--color-accent-primary);
          color: var(--color-accent-contrast);
          border-color: var(--color-accent-primary);
        }
        .probe-main {
          min-height: 0;
          display: grid;
          grid-template-columns: minmax(280px, 360px) minmax(420px, 1fr) minmax(340px, 460px);
          gap: 12px;
          padding: 12px;
        }
        .probe-panel {
          min-height: 0;
          overflow: hidden;
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-secondary);
          border-radius: 10px;
          display: flex;
          flex-direction: column;
        }
        .probe-panel-head {
          padding: 12px 14px;
          border-bottom: 1px solid var(--color-border-primary);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .probe-panel-body {
          min-height: 0;
          overflow: auto;
          padding: 12px;
        }
        .probe-tabs {
          display: flex;
          gap: 8px;
          padding: 10px 12px 0;
          flex-wrap: wrap;
        }
        .probe-tab {
          height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-tertiary);
          color: var(--color-text-muted);
          font-size: 12px;
        }
        .probe-tab.active {
          color: var(--color-accent-primary);
          border-color: var(--color-accent-primary);
          background: var(--color-accent-light);
        }
        .probe-case {
          width: 100%;
          text-align: left;
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-tertiary);
          border-radius: 9px;
          padding: 10px;
          margin-bottom: 8px;
          color: var(--color-text-primary);
          display: grid;
          gap: 8px;
        }
        .probe-case.active {
          border-color: var(--color-accent-primary);
          background: var(--color-accent-light);
        }
        .probe-case-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .probe-case-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .probe-textarea {
          width: 100%;
          min-height: 280px;
          resize: vertical;
          padding: 12px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 12px;
          line-height: 1.55;
        }
        .probe-pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 12px;
          line-height: 1.55;
          color: var(--color-text-secondary);
        }
        .probe-preview-image {
          max-width: 100%;
          max-height: 280px;
          object-fit: contain;
          border-radius: 8px;
          border: 1px solid var(--color-border-primary);
          background: var(--color-bg-primary);
          margin-bottom: 10px;
        }
        @media (max-width: 1280px) {
          .probe-main {
            grid-template-columns: 300px minmax(420px, 1fr);
          }
          .probe-main .probe-panel.detail {
            grid-column: 1 / -1;
            min-height: 360px;
          }
        }
      `}</style>

      <header className="probe-header">
        <div className="probe-title">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
            <ServerCog size={20} />
          </div>
          <div>
            <div className="text-base font-semibold">代理能力测试</div>
            <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>逐项验证文字、图片、视频、音频能力，所有测试走总闸</div>
          </div>
        </div>
        <div className="probe-toolbar">
          <AppSelect className="probe-select min-w-[170px]" value={fixedVideoProvider || provider} onChange={(e) => setProvider(e.target.value)} disabled={Boolean(fixedVideoProvider)} title={fixedVideoProvider ? `此测试项固定使用 ${providerLabelMap.get(fixedVideoProvider)?.name || fixedVideoProvider}` : undefined}>
            {!providers.some((item) => item.id === (fixedVideoProvider || provider)) && <option value={fixedVideoProvider || provider}>{providerLabelMap.get(fixedVideoProvider || provider)?.name || provider}</option>}
            {providers.map((item) => (
              <option key={item.id} value={item.id}>
                {item.desc ? `${item.name} - ${item.desc}` : item.name}
              </option>
            ))}
          </AppSelect>
          {isOfficialModelarkSelected && (
            <button className="probe-button" type="button" onClick={checkModelarkConfiguration} disabled={configurationCheck?.loading} title="仅检查当前服务器配置，不提交生成任务">
              <ServerCog size={14} /> 配置检查
            </button>
          )}
          {isDreamflyImage && !fixedVideoProvider && (
            <AppSelect
              className="probe-select min-w-[250px]"
              value={effectiveDreamflyImageModel}
              onChange={(event) => setDreamflyImageModel(event.target.value)}
              disabled={loadingDreamflyImageModels}
              aria-label="DreamFly 图片模型"
              title={dreamflyImageModelError || "选择本次代理测试实际使用的 DreamFly 图片模型"}
            >
              {dreamflyImageModels.map((item) => (
                <option key={item.id} value={item.id}>{item.label || item.id}</option>
              ))}
            </AppSelect>
          )}
          {isDreamflyImage && !fixedVideoProvider && dreamflyImageModelError && (
            <span
              className="text-[11px]"
              style={{ color: "var(--color-warning)" }}
              title={dreamflyImageModelError}
            >
              使用内置模型目录
            </span>
          )}
          <input className="probe-input w-24" value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="projectId" />
          <input className="probe-input w-24" value={episodeId} onChange={(e) => setEpisodeId(e.target.value)} placeholder="episodeId" />
          <button className="probe-button" type="button" onClick={loadCases} disabled={loadingCases}>
            <RefreshCw size={14} className={loadingCases ? "animate-spin" : ""} /> 刷新
          </button>
          <button className="probe-button primary" type="button" onClick={runAll} disabled={runningIds.size > 0 || filteredCases.length === 0}>
            <Play size={14} /> 顺序跑当前分类
          </button>
        </div>
      </header>

      <main className="probe-main">
        <section className="probe-panel">
          <div className="probe-panel-head">
            <div>
              <div className="text-sm font-semibold">测试项</div>
              <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>{filteredCases.length} / {cases.length}</div>
            </div>
          </div>
          <div className="probe-tabs">
            {["all", "text", "image", "video", "audio"].map((key) => (
              <button key={key} className={`probe-tab ${category === key ? "active" : ""}`} type="button" onClick={() => setCategory(key)}>
                {key === "all" ? "全部" : CATEGORY_LABELS[key]}
              </button>
            ))}
          </div>
          <div className="probe-panel-body">
            {filteredCases.map((item) => {
              const result = results[item.id];
              const status = runningIds.has(item.id) ? "running" : (result?.status || "idle");
              return (
                <button key={item.id} type="button" className={`probe-case ${selectedCase?.id === item.id ? "active" : ""}`} onClick={() => setSelectedCaseId(item.id)}>
                  <div className="probe-case-top">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{item.name}</div>
                      <div className="text-[11px] mt-1" style={{ color: "var(--color-text-muted)" }}>{CATEGORY_LABELS[item.category] || item.category}</div>
                    </div>
                    <StatusPill status={status} />
                  </div>
                  <div className="text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{item.description}</div>
                  <div className="probe-case-actions">
                    <span className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                      {result?.durationMs != null ? `${result.durationMs} ms` : "等待运行"}
                    </span>
                    <span
                      className="probe-button h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        runCase(item);
                      }}
                    >
                      {runningIds.has(item.id) ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} 运行
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="probe-panel">
          <div className="probe-panel-head">
            <div>
              <div className="text-sm font-semibold">{selectedCase?.name || "未选择测试项"}</div>
              <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>{selectedCase?.description}</div>
            </div>
            <div className="flex items-center gap-2">
              {selectedCase?.fixedProvider && (
                <span className="text-[11px] rounded-full px-2 py-1" style={{ color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                  固定 {selectedCase.fixedProvider} · {selectedCase.fixedModel} · {selectedCase.fixedResolution}
                </span>
              )}
              <button className="probe-button" type="button" onClick={resetParams} disabled={!selectedCase}>
                <RefreshCw size={14} /> 重置参数
              </button>
              <button className="probe-button primary" type="button" onClick={() => selectedCase && runCase(selectedCase)} disabled={!selectedCase || runningIds.has(selectedCase.id)}>
                {selectedCase && runningIds.has(selectedCase.id) ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} 运行当前
              </button>
            </div>
          </div>
          <div className="probe-panel-body">
            <div className="flex items-center gap-2 mb-2 text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
              {selectedTextInputKey ? <FileText size={14} /> : <FileJson size={14} />}
              {selectedTextInputKey ? `长文本输入（自动作为 ${selectedTextInputKey} 传参）` : "传参 JSON"}
            </div>
            <textarea
              className="probe-textarea"
              value={selectedCase ? (paramDrafts[selectedCase.id] ?? (selectedTextInputKey ? "" : "{}")) : ""}
              onChange={(e) => {
                if (!selectedCase) return;
                setParamDrafts((prev) => ({ ...prev, [selectedCase.id]: e.target.value }));
              }}
              spellCheck={false}
              placeholder={selectedTextInputKey ? "直接粘贴小说、剧本或长上下文，不需要写 JSON。" : "输入 JSON 参数。"}
            />
            {isDomesticAssetLibrarySelected && (
              <ModelArkReferenceLibraryPanel projectId={projectId || undefined} provider={selectedModelarkProvider} references={selectedModelarkReferences} />
            )}
            {selectedResult && (
              <div className="mt-4 rounded-lg border p-3 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {selectedResult.status === "success" ? <CheckCircle2 size={16} style={{ color: "var(--color-success)" }} /> : <AlertTriangle size={16} style={{ color: "var(--color-error)" }} />}
                    {fixedVideoProvider ? '提交结果' : '最近结果'}
                  </div>
                  <StatusPill status={selectedResult.status} />
                </div>
                {selectedVideoTask && (
                  <button className="probe-button mb-2" type="button" onClick={() => querySubmittedVideo(selectedCase)} disabled={queryingVideoTasks.has(`${selectedVideoTask.provider}:${selectedVideoTask.taskId}`)}>
                    <RefreshCw size={13} className={queryingVideoTasks.has(`${selectedVideoTask.provider}:${selectedVideoTask.taskId}`) ? "animate-spin" : ""} /> 查询任务
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  <div>耗时: {selectedResult.durationMs ?? 0} ms</div>
                  <div>完成: {selectedResult.finishedAt || "-"}</div>
                </div>
                {selectedPreviewSrc && (
                  <img className="probe-preview-image mt-3 animate-in fade-in duration-300" src={selectedPreviewSrc} alt="probe-result" />
                )}
                <pre className="probe-pre mt-3">{truncate(pretty(selectedResult.result || selectedResult.error), 1200)}</pre>
                {selectedResult.videoTaskStatus && <pre className="probe-pre mt-3">{pretty(selectedResult.videoTaskStatus)}</pre>}
              </div>
            )}
          </div>
        </section>

        <section className="probe-panel detail">
          <div className="probe-panel-head">
            <div className="flex items-center gap-2">
              <Terminal size={16} />
              <div className="text-sm font-semibold">详细信息</div>
            </div>
            <button className="probe-button" type="button" onClick={() => copyDetail(selectedResult || {})}>
              <Copy size={14} /> 复制
            </button>
          </div>
          <div className="probe-panel-body">
            {!selectedResult ? (
              <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--color-text-muted)" }}>
                运行任意测试后，这里会显示完整入参、响应和错误堆栈。
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2 text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>
                    <ChevronRight size={14} /> 请求快照
                  </div>
                  <pre className="probe-pre">{pretty(selectedResult.request)}</pre>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2 text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>
                    <ChevronRight size={14} /> 返回结果
                  </div>
                  <pre className="probe-pre">{pretty(selectedResult.result)}</pre>
                </div>
                {selectedResult.videoTaskStatus && (
                  <div>
                    <div className="flex items-center gap-2 mb-2 text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>
                      <ChevronRight size={14} /> 视频任务状态
                    </div>
                    <pre className="probe-pre">{pretty(selectedResult.videoTaskStatus)}</pre>
                  </div>
                )}
                {selectedResult.error && (
                  <div>
                    <div className="flex items-center gap-2 mb-2 text-xs font-semibold" style={{ color: "var(--color-error)" }}>
                      <XCircle size={14} /> 错误详情
                    </div>
                    <pre className="probe-pre">{pretty(selectedResult.error)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Modal isOpen={configurationCheckOpen} onClose={() => setConfigurationCheckOpen(false)} title={`${providerLabelMap.get(configurationCheck?.provider)?.name || "视频线路"} · 配置检查`} width="max-w-2xl">
        <div className="space-y-4 text-sm" style={{ color: "var(--color-text-secondary)" }}>
          <p>仅检查当前服务器配置，不提交生成任务。配置已填写不代表上游鉴权成功；本页不显示或接收完整 Key。</p>
          {configurationCheck?.loading && <p role="status" className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> 正在读取当前服务器配置…</p>}
          {configurationCheck?.error && <p role="alert" style={{ color: "var(--color-error)" }}>{configurationCheck.error}</p>}
          {configurationCheck?.configuration && (
            <>
              <p style={{ color: configurationCheck.configuration.configurationComplete ? "var(--color-success)" : "var(--color-warning)" }}>
                {configurationCheck.configuration.configurationComplete ? "必要配置已填写，尚未验证上游鉴权。" : "当前服务器必要配置不完整。"}
              </p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 break-all">
                <dt>API 地址</dt><dd>{configurationCheck.configuration.baseUrl || "未配置"}</dd>
                <dt>区域</dt><dd>{configurationCheck.configuration.region || "未配置"}</dd>
                <dt>实际模型 / Endpoint</dt><dd>{configurationCheck.configuration.model || "未配置"}</dd>
                {configurationCheck.configuration.models?.map((model) => <React.Fragment key={model.model}><dt>{model.label}</dt><dd>{model.endpoint || '未配置'}</dd></React.Fragment>)}
                <dt>视频 API Key</dt><dd>{configurationCheck.configuration.keyConfigured ? "已配置（不代表有效）" : "未配置"}</dd>
                <dt>生效 Key 来源</dt><dd>{MODELARK_KEY_SOURCE_LABELS[configurationCheck.configuration.keySource] || "未知"}</dd>
                <dt>私有配置文件</dt><dd>{configurationCheck.privateConfigAvailable ? "可读取" : "未读取到"}</dd>
                <dt>素材策略</dt><dd>{configurationCheck.configuration.referencePolicy === "original_url"
                  ? "2.5 直接使用原始图片 URL，不查询素材库、不需要 AK/SK 或 Asset ID"
                  : configurationCheck.configuration.referencePolicy === "provider_assets"
                    ? "2.0 参考图需入国内官方素材库，需要对应库 AK/SK"
                    : "当前服务端未返回可识别的素材策略，请更新后端"}</dd>
              </dl>
              {configurationCheck.configuration.keyOverrideDiffers && <p role="alert" style={{ color: "var(--color-warning)" }}>当前生效的是后台在线 Key，与应用配置不同；修改配置文件或重启不会替换此在线覆盖。</p>}
              {[...(Array.isArray(configurationCheck.configuration.hints) ? configurationCheck.configuration.hints : []), ...configurationCheck.notes].map((note, index) => (
                <p key={index} className="text-xs">{note}</p>
              ))}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ProviderProbePage;
